import { create } from 'zustand';
import type { CmsLoginUser } from '../types/auth.types';
import type { RrmsRole } from '../config/app.config';

export interface AuthState {
  user: CmsLoginUser | null;
  token: string | null;
  role: RrmsRole | null;
  permissions: string[];
  isSuperAdmin: boolean;
  isAuthenticated: boolean;
  login: (payload: { user: CmsLoginUser; token: string }) => void;
  logout: () => void;
  /** Dynamic-RBAC check: true for super admin or when the permission is held. */
  has: (permissionKey: string) => boolean;
  /**
   * Permission check with a legacy-role fallback: portal sessions are judged by
   * their dynamic permission set; legacy CMS/bypass sessions (no permissions)
   * fall back to the given roles. Super admin always passes.
   */
  can: (permissionKey: string, fallbackRoles?: RrmsRole[]) => boolean;
}

function normaliseRoleOld(roleTitle: string | null | undefined): RrmsRole | null {
  const upper = (roleTitle || '').toUpperCase().replace(/\s+/g, '_');
  if (
    upper === 'RRMS_USER' ||
    upper === 'RRMS_CONTENT_MANAGER' ||
    upper === 'RRMS_SUPER_ADMIN'
  ) {
    return upper as RrmsRole;
  }
  if (upper.includes('ADMIN') || upper.includes('SUPER')) {
    return 'RRMS_SUPER_ADMIN';
  }
  if (upper.includes('CONTENT') && upper.includes('MANAGER')) {
    return 'RRMS_CONTENT_MANAGER';
  }
  if (upper.includes('RRMS')) {
    return 'RRMS_USER';
  }
  if (!roleTitle || roleTitle.trim() === '') {
    return 'RRMS_USER';
  }
  return null;
}

function normaliseRole(roleTitle: string | null | undefined): RrmsRole | null {
  const raw = roleTitle || '';
  const upper = raw.toUpperCase().replace(/\s+/g, '_');

  // 1. Exact RRMS roles if CMS ever sends them
  if (upper === 'RRMS_USER' || upper === 'RRMS_CONTENT_MANAGER' || upper === 'RRMS_SUPER_ADMIN') {
    return upper as RrmsRole;
  }

  // 2. Admin / super → RRMS_SUPER_ADMIN
  if (upper.includes('ADMIN') || upper.includes('SUPER')) {
    return 'RRMS_SUPER_ADMIN';
  }

  // 3. Content manager → RRMS_CONTENT_MANAGER
  if (upper.includes('CONTENT') && upper.includes('MANAGER')) {
    return 'RRMS_CONTENT_MANAGER';
  }

  // 4. If there is any role text at all, treat as RRMS_USER
  if (raw.trim() !== '') {
    return 'RRMS_USER';
  }

  // 5. No role at all → still RRMS_USER by your rule
  return 'RRMS_USER';
}

function getInitialAuthState() {
  if (typeof window === 'undefined') {
    return {
      user: null,
      token: null,
      role: null,
      permissions: [],
      isSuperAdmin: false,
      isAuthenticated: false,
    };
  }

  const token = localStorage.getItem('rrms_token');
  const storedUser = localStorage.getItem('rrms_user');
  const storedRole = localStorage.getItem('rrms_role');

  let user: CmsLoginUser | null = null;
  if (storedUser) {
    try {
      user = JSON.parse(storedUser) as CmsLoginUser;
    } catch {
      user = null;
    }
  }

  const role = normaliseRole(storedRole || user?.role_title);
  const permissions = user?.permissions ?? [];
  const isSuperAdmin = user?.is_super_admin ?? false;
  const isAuthenticated = Boolean(token && user && role);

  return {
    user,
    token,
    role,
    permissions,
    isSuperAdmin,
    isAuthenticated,
  };
}

export const useAuthStore = create<AuthState>((set, get) => ({
  ...getInitialAuthState(),
  login: ({ user, token }) => {
    const role = normaliseRole(user.role_title);
    const permissions = user.permissions ?? [];
    const isSuperAdmin = user.is_super_admin ?? false;

    if (role) {
      localStorage.setItem('rrms_token', token);
      localStorage.setItem('rrms_user', JSON.stringify(user));
      localStorage.setItem('rrms_role', role);
      if (user.division_id != null) {
        localStorage.setItem('rrms_division_id', String(user.division_id));
      }
      if (user.department_id != null) {
        localStorage.setItem('rrms_department_id', String(user.department_id));
      }
      if (user.designation_id != null) {
        localStorage.setItem('rrms_designation_id', String(user.designation_id));
      }
    }

    set({
      user,
      token,
      role,
      permissions,
      isSuperAdmin,
      isAuthenticated: !!token && !!role,
    });
  },
  logout: () => {
    localStorage.removeItem('rrms_token');
    localStorage.removeItem('rrms_user');
    localStorage.removeItem('rrms_role');
    localStorage.removeItem('rrms_division_id');
    localStorage.removeItem('rrms_department_id');
    localStorage.removeItem('rrms_designation_id');
    set({
      user: null,
      token: null,
      role: null,
      permissions: [],
      isSuperAdmin: false,
      isAuthenticated: false,
    });
  },
  has: (permissionKey) => {
    const { isSuperAdmin, permissions } = get();
    return isSuperAdmin || permissions.includes(permissionKey);
  },
  can: (permissionKey, fallbackRoles = []) => {
    const { isSuperAdmin, permissions, role } = get();
    if (isSuperAdmin) return true;
    if (permissions.length > 0) return permissions.includes(permissionKey);
    return role != null && fallbackRoles.includes(role);
  },
}));

